import { forwardRef, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react';
import s from './Button.module.css';
import { cx } from './cx';

export type ButtonTone = 'accent' | 'teal' | 'orange' | 'red' | 'blue' | 'gold' | 'pink' | 'neutral';
export type ButtonVariant = 'solid' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** second line, smaller, tabular digits (e.g. "20 tokens", "50 × 2.47") */
  sub?: ReactNode;
  block?: boolean;
  /** i18n rule: text buttons get min-width, never width */
  minWidth?: number | string;
}

/**
 * Button: accent = the call to action of the screen (teal, or the game's palette inside its material: red-gold for the
 * Chinese games), teal = primary, orange = daily bonus / cash-out, red = danger, neutral outline = secondary.
 * Lit gradient + blick + soft shadow, hover lifts 1px, press sinks 1px (no movement with reduced motion),
 * disabled → 45% opacity, flat.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { tone = 'teal', variant = 'solid', size = 'md', sub, block, minWidth, className, style, children, type = 'button', ...rest },
  ref,
) {
  const st: CSSProperties | undefined = minWidth !== undefined ? { minWidth, ...style } : style;
  return (
    <button
      ref={ref}
      type={type}
      className={cx(s.btn, s[tone], s[variant], s[size], block && s.block, className)}
      style={st}
      {...rest}
    >
      <span>{children}</span>
      {sub !== undefined && sub !== null && <span className={s.sub}>{sub}</span>}
    </button>
  );
});

export const buttonClasses = s;
